/**
 * Integrations — connect external tools and bring their data in, and (for
 * Google Calendar only) push a narrow slice of app-owned data back out.
 *
 * Covers three very different shapes of integration:
 *   1. One-shot file imports (Clockify CSV, Calendar ICS) — upload and forget.
 *   2. OAuth-connected accounts (Google Calendar, Google Tasks) — connect
 *      once, then trigger manual syncs from this page.
 *   3. Calendar write-back — the one direction that changes something outside
 *      this app. Its surface is built around that asymmetry: preview is the
 *      primary action and sends nothing, Apply is secondary and confirmed, and
 *      the journal shows every attempt including the ones whose outcome is
 *      unknown.
 *
 * Clockify and Google Tasks land in actual `time_entries`; calendar imports
 * land in planned events so schedules do not masquerade as tracked work.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import GoogleIcon from '@mui/icons-material/Google';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from '../utils/apiError';
import PageShell from '../components/PageShell';
import SpreadsheetImportDialog from '../components/SpreadsheetImportDialog';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type Provider = 'google_calendar' | 'google_tasks';

interface IntegrationAccount {
  id: number;
  provider: Provider;
  account_email?: string | null;
  last_sync_at?: string | null;
  last_sync_status?: string | null;
  last_sync_error?: string | null;
  created_at: string;
}

interface ImportResult {
  provider: string;
  imported: number;
  skipped: number;
  errors: string[];
}

interface SyncResult {
  provider: Provider;
  imported: number;
  skipped: number;
  synced_at: string;
  errors: string[];
}

// --- Calendar write-back -----------------------------------------------

type CalendarSyncAction = 'create' | 'patch' | 'delete' | 'skip';
type CalendarSyncPhase = 'intent' | 'result';

interface CalendarWriteReadiness {
  ok: boolean;
  reason: string;
  message: string;
  reconnect_required: boolean;
}

interface CalendarSyncPlanItem {
  action: CalendarSyncAction;
  occurrence_id: number;
  external_calendar_id: string;
  external_event_id?: string | null;
  reason: string;
  title?: string | null;
  start_at?: string | null;
  end_at?: string | null;
}

interface CalendarSyncPreview {
  window_days: number;
  readiness: CalendarWriteReadiness;
  creates: number;
  patches: number;
  deletes: number;
  items: CalendarSyncPlanItem[];
}

interface CalendarSyncApplyResult {
  applied: boolean;
  creates: number;
  patches: number;
  deletes: number;
  failures: number;
  items: CalendarSyncPlanItem[];
  detail?: string | null;
}

interface CalendarSyncJournalEntry {
  id: number;
  action: CalendarSyncAction;
  phase: CalendarSyncPhase;
  succeeded?: boolean | null;
  provider: string;
  occurrence_id?: number | null;
  external_calendar_id?: string | null;
  external_event_id?: string | null;
  reason?: string | null;
  request_summary?: string | null;
  response_summary?: string | null;
  error?: string | null;
  created_at?: string | null;
}

const WINDOW_DAY_CHOICES = [1, 3, 7, 14, 30];
const DEFAULT_WINDOW_DAYS = 3;
const JOURNAL_LIMIT = 100;

const ACTION_LABELS: Record<CalendarSyncAction, string> = {
  create: 'Create',
  patch: 'Update',
  delete: 'Delete',
  skip: 'Leave as is',
};

const ACTION_COLORS: Record<
  CalendarSyncAction,
  'success' | 'info' | 'warning' | 'default'
> = {
  create: 'success',
  patch: 'info',
  delete: 'warning',
  skip: 'default',
};

const PHASE_LABELS: Record<CalendarSyncPhase, string> = {
  intent: 'Intent',
  result: 'Result',
};

// The safety property this whole section rests on, stated once and reused so
// the preview card and the Apply confirmation cannot drift apart.
const OWNERSHIP_PROMISE =
  'Only events this app created are ever touched. Every event is stamped with an ' +
  'ownership marker when it is made, and that marker is re-read from Google and ' +
  'checked before anything is updated or removed. Events already on your calendar ' +
  'that this app did not create are never modified and never deleted.';

const formatWindowDays = (days: number): string =>
  days === 1 ? '1 day' : `${days} days`;

const formatStamp = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const formatRange = (start?: string | null, end?: string | null): string => {
  if (!start) return '—';
  const startDate = new Date(start);
  if (Number.isNaN(startDate.getTime())) return start;
  const startText = startDate.toLocaleString();
  if (!end) return startText;
  const endDate = new Date(end);
  if (Number.isNaN(endDate.getTime())) return startText;
  return `${startText} – ${endDate.toLocaleTimeString()}`;
};

// The plan in the owner's own terms. Counts cover the actionable items only —
// SKIP rows are listed below but are, by definition, not a change.
const planSummary = (preview: CalendarSyncPreview): string => {
  const total = preview.creates + preview.patches + preview.deletes;
  if (total === 0) {
    return (
      'Nothing to do — your calendar already matches the next ' +
      `${formatWindowDays(preview.window_days)}.`
    );
  }
  return (
    `Would create ${preview.creates}, patch ${preview.patches}, ` +
    `delete ${preview.deletes} — all app-owned.`
  );
};

const applySummary = (result: CalendarSyncApplyResult): string => {
  if (!result.applied) {
    return result.detail || 'Nothing on your calendar was changed.';
  }
  const base =
    `Created ${result.creates}, patched ${result.patches}, ` +
    `deleted ${result.deletes}.`;
  return result.failures > 0
    ? `${base} ${result.failures} item(s) failed — see the journal below.`
    : base;
};

/**
 * Ids of INTENT rows that no RESULT row answers.
 *
 * The journal carries no correlation id, so the pairing is positional: within
 * one (provider, action, occurrence) key, results answer intents oldest-first.
 * That is sound here because row ids are monotonic and a mutation's RESULT is
 * always written after its INTENT — so an intent left over at the end of this
 * walk genuinely has no result on record.
 *
 * Truncation cannot manufacture a false positive either. The endpoint returns
 * the newest `limit` rows, so it drops intents before it drops the results that
 * answer them; the worst it can produce is an orphaned RESULT, which this
 * function ignores. An "outcome unknown" row therefore always means what it
 * says: the app started a call and never got to write down how it ended.
 */
const unresolvedIntentIds = (
  entries: CalendarSyncJournalEntry[],
): Set<number> => {
  const pending = new Map<string, number[]>();
  [...entries]
    .sort((a, b) => a.id - b.id)
    .forEach((entry) => {
      const key = `${entry.provider}|${entry.action}|${entry.occurrence_id ?? 'none'}`;
      const queue = pending.get(key) ?? [];
      if (entry.phase === 'intent') {
        queue.push(entry.id);
      } else if (queue.length > 0) {
        queue.shift();
      }
      pending.set(key, queue);
    });
  const unresolved = new Set<number>();
  pending.forEach((queue) => queue.forEach((id) => unresolved.add(id)));
  return unresolved;
};

// What a journal row says happened. INTENT rows carry no outcome of their own
// — theirs is whichever RESULT answered them, or nothing at all.
const journalOutcome = (
  entry: CalendarSyncJournalEntry,
  unresolved: Set<number>,
): { label: string; color: 'success' | 'error' | 'warning' | 'default' } => {
  if (entry.phase === 'result') {
    if (entry.succeeded === true) return { label: 'Succeeded', color: 'success' };
    if (entry.succeeded === false) return { label: 'Failed', color: 'error' };
    return { label: 'Recorded', color: 'default' };
  }
  return unresolved.has(entry.id)
    ? { label: 'Outcome unknown', color: 'warning' }
    : // Rows are newest first, and a result is always written after its
      // intent, so the answering row sits above this one.
      { label: 'Answered above', color: 'default' };
};

// The section-header chip. "Read-only for now" rather than a warning colour on
// purpose: a read-only connection is the expected state of every account that
// predates write-back, not a fault to be flagged.
const writeStatusChipFor = (
  readiness: CalendarWriteReadiness | null,
): { label: string; color: 'success' | 'default' } => {
  if (readiness?.ok === true) return { label: 'Write-back authorised', color: 'success' };
  if (readiness?.reconnect_required === true) {
    return { label: 'Read-only for now', color: 'default' };
  }
  return { label: 'Permission unknown', color: 'default' };
};

const PROVIDER_LABELS: Record<Provider, string> = {
  google_calendar: 'Google Calendar',
  google_tasks: 'Google Tasks',
};

const Integrations: React.FC = () => {
  const {
    cardContentPaddingSx,
    dialogContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    sectionGapSx,
    tableCellPaddingSx,
  } = useLayoutTokens();
  const [accounts, setAccounts] = useState<IntegrationAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [readiness, setReadiness] = useState<CalendarWriteReadiness | null>(null);
  const [readinessError, setReadinessError] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS);
  const [preview, setPreview] = useState<CalendarSyncPreview | null>(null);
  // A plan describes the calendar as it was when the preview ran. Applying
  // changes that, so the plan on screen is marked stale rather than silently
  // refetched — a second live read the owner did not ask for is exactly the
  // kind of surprise this section is built to avoid.
  const [previewStale, setPreviewStale] = useState(false);
  const [confirmApplyOpen, setConfirmApplyOpen] = useState(false);
  const [applyResult, setApplyResult] = useState<CalendarSyncApplyResult | null>(null);
  const [journal, setJournal] = useState<CalendarSyncJournalEntry[]>([]);
  const [journalError, setJournalError] = useState<string | null>(null);

  const clockifyInputRef = useRef<HTMLInputElement | null>(null);
  const calendarIcsInputRef = useRef<HTMLInputElement | null>(null);
  const loopHabitsInputRef = useRef<HTMLInputElement | null>(null);
  const spreadsheetInputRef = useRef<HTMLInputElement | null>(null);
  const [spreadsheetFile, setSpreadsheetFile] = useState<File | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const fetchAccounts = useCallback(async () => {
    const response = await axios.get<IntegrationAccount[]>(
      `${API_BASE_URL}/api/integrations/`,
    );
    setAccounts(response.data);
  }, []);

  // Never calls Google — the answer comes from the scope already stored with
  // the account, which is what lets the banner appear before the owner
  // attempts anything.
  const fetchReadiness = useCallback(async () => {
    try {
      const response = await axios.get<CalendarWriteReadiness>(
        `${API_BASE_URL}/api/calendar-sync/readiness`,
      );
      setReadiness(response.data);
      setReadinessError(null);
    } catch (err: any) {
      // Say we do not know rather than implying write-back is fine: silence
      // here would read as "no reconnect needed".
      setReadiness(null);
      setReadinessError(
        getApiErrorMessage(err, 'Could not check calendar write-back permission'),
      );
    }
  }, []);

  const fetchJournal = useCallback(async () => {
    try {
      const response = await axios.get<CalendarSyncJournalEntry[]>(
        `${API_BASE_URL}/api/calendar-sync/journal`,
        { params: { limit: JOURNAL_LIMIT } },
      );
      setJournal(Array.isArray(response.data) ? response.data : []);
      setJournalError(null);
    } catch (err: any) {
      setJournal([]);
      setJournalError(getApiErrorMessage(err, 'Could not load the calendar sync journal'));
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await fetchAccounts();
        setError(null);
      } catch (err: any) {
        setError(getApiErrorMessage(err, 'Failed to load integrations'));
      } finally {
        setLoading(false);
      }
      // Write-back status loads alongside, never in front: both fetchers keep
      // their own failures, so a calendar-sync outage cannot stop the file
      // imports above from working.
      await Promise.all([fetchReadiness(), fetchJournal()]);
    })();
  }, [fetchAccounts, fetchJournal, fetchReadiness]);

  // Read ?connected=<provider> / ?error=<code> set by the OAuth callback
  // and surface a user-facing message, then strip it from the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get('connected');
    const err = params.get('error');
    if (connected) {
      setInfo(`${PROVIDER_LABELS[connected as Provider] ?? connected} connected.`);
    }
    if (err) {
      setError(`Google authorization failed: ${err}`);
    }
    if (connected || err) {
      navigate('/integrations', { replace: true });
    }
  }, [location.search, navigate]);

  const accountFor = (provider: Provider): IntegrationAccount | undefined =>
    accounts.find((a) => a.provider === provider);

  const handleConnectGoogle = async (provider: Provider) => {
    try {
      setBusy(`connect:${provider}`);
      const response = await axios.get<{ authorization_url: string }>(
        `${API_BASE_URL}/api/integrations/google/authorize`,
        { params: { provider } },
      );
      window.location.href = response.data.authorization_url;
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to start Google authorization'));
      setBusy(null);
    }
  };

  const handleDisconnect = async (account: IntegrationAccount) => {
    try {
      setBusy(`disconnect:${account.id}`);
      await axios.delete(`${API_BASE_URL}/api/integrations/${account.id}`);
      setInfo(`${PROVIDER_LABELS[account.provider]} disconnected.`);
      await fetchAccounts();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to disconnect integration'));
    } finally {
      setBusy(null);
    }
  };

  const handleSync = async (provider: Provider) => {
    const endpoint =
      provider === 'google_calendar'
        ? '/api/integrations/google-calendar/sync'
        : '/api/integrations/google-tasks/sync';
    try {
      setBusy(`sync:${provider}`);
      const response = await axios.post<SyncResult>(`${API_BASE_URL}${endpoint}`);
      const { imported, skipped, errors } = response.data;
      setInfo(
        `${PROVIDER_LABELS[provider]}: imported ${imported}, skipped ${skipped}` +
          (errors.length ? `, ${errors.length} error(s)` : '.'),
      );
      await fetchAccounts();
    } catch (err: any) {
      setError(getApiErrorMessage(err, `Failed to sync ${PROVIDER_LABELS[provider]}`));
    } finally {
      setBusy(null);
    }
  };

  // A dry run. The endpoint constructs no transport, so there is no code path
  // from this button to the network — it works on a read-only account on
  // purpose, because seeing the plan is how the owner decides whether
  // reconnecting is worth it.
  const handlePreview = async () => {
    try {
      setBusy('calendar:preview');
      const response = await axios.get<CalendarSyncPreview>(
        `${API_BASE_URL}/api/calendar-sync/preview`,
        { params: { window_days: windowDays } },
      );
      setPreview(response.data);
      setPreviewStale(false);
      setApplyResult(null);
      // The preview carries its own readiness, so the banner tracks a scope
      // that changed since page load without a second round trip.
      if (response.data?.readiness) {
        setReadiness(response.data.readiness);
        setReadinessError(null);
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to preview calendar sync'));
    } finally {
      setBusy(null);
    }
  };

  const handleApply = async () => {
    setConfirmApplyOpen(false);
    try {
      setBusy('calendar:apply');
      const response = await axios.post<CalendarSyncApplyResult>(
        `${API_BASE_URL}/api/calendar-sync/apply`,
        null,
        { params: { window_days: windowDays } },
      );
      setApplyResult(response.data);
      setPreviewStale(true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Calendar write-back failed'));
    } finally {
      setBusy(null);
      // The journal is the record of what actually happened out there, so it
      // is refreshed whether the request succeeded or blew up.
      await fetchJournal();
    }
  };

  const handleRefreshJournal = async () => {
    setBusy('calendar:journal');
    await fetchJournal();
    setBusy(null);
  };

  const handleUpload = async (
    key: 'clockify' | 'calendar' | 'loop-habits',
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    if (key === 'loop-habits') {
      formData.append('tz_offset', String(new Date().getTimezoneOffset()));
    }
    const labels: Record<string, string> = {
      clockify: 'Clockify',
      calendar: 'Calendar ICS',
      'loop-habits': 'Loop Habits',
    };
    try {
      setBusy(`upload:${key}`);
      const response = await axios.post<ImportResult>(
        `${API_BASE_URL}/api/integrations/${key}/import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const { imported, skipped, errors } = response.data;
      setInfo(
        `${labels[key]} import: imported ${imported}, skipped ${skipped}` +
          (errors.length ? `, ${errors.length} row(s) skipped with errors` : '.'),
      );
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Import failed'));
    } finally {
      setBusy(null);
      // Reset the input so the same file can be re-uploaded.
      if (event.target) event.target.value = '';
    }
  };

  if (loading) {
    return (
      <PageShell>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  const formatSyncStamp = (iso?: string | null): string => {
    if (!iso) return 'Never synced';
    return `Last synced ${new Date(iso).toLocaleString()}`;
  };

  const writeStatusChip = writeStatusChipFor(readiness);
  const calendarUnconnected = readiness?.reason === 'not_connected';
  const actionableCount = preview
    ? preview.creates + preview.patches + preview.deletes
    : 0;
  const unresolvedIntents = unresolvedIntentIds(journal);

  return (
    <PageShell>
      <Box sx={pageHeaderSx}>
        <Typography variant="h3" component="h1">
          Integrations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Import time tracking data from Clockify or any spreadsheet export,
          import planned calendar events from any ICS file (Google Calendar,
          Outlook, Apple Calendar, etc.), or import habits from Loop Habit
          Tracker. Connect Google Calendar and Google Tasks to sync on demand.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={sectionGapSx} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Typography variant="h5" sx={{ mb: 2 }}>
        File imports
      </Typography>
      <Grid container spacing={pageGridSpacing} sx={sectionGapSx}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h6">Clockify (CSV)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a Clockify <em>Detailed Report</em> CSV export. Existing
                rows (matched by Clockify ID) are skipped so re-uploads are safe.
              </Typography>
              <input
                ref={clockifyInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(e) => handleUpload('clockify', e)}
              />
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => clockifyInputRef.current?.click()}
                disabled={busy === 'upload:clockify'}
              >
                {busy === 'upload:clockify' ? <CircularProgress size={20} /> : 'Upload CSV'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h6">Calendar Import (ICS)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a <code>.ics</code> calendar file from any provider
                (Google Calendar, Outlook, Apple Calendar, etc.). Each event
                becomes a planned event; repeat uploads are deduplicated on
                event UID.
              </Typography>
              <input
                ref={calendarIcsInputRef}
                type="file"
                accept=".ics,.ical"
                style={{ display: 'none' }}
                onChange={(e) => handleUpload('calendar', e)}
              />
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => calendarIcsInputRef.current?.click()}
                disabled={busy === 'upload:calendar'}
              >
                {busy === 'upload:calendar' ? <CircularProgress size={20} /> : 'Upload ICS'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h6">Spreadsheet (CSV)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Import time entries from any spreadsheet export — Toggl,
                Harvest, Google Sheets, hand-kept timesheets. Pick the file,
                map its columns, review a validated preview (with overlap
                warnings), then import. Re-imports skip rows already imported.
              </Typography>
              <input
                ref={spreadsheetInputRef}
                type="file"
                accept=".csv,.xlsx"
                style={{ display: 'none' }}
                data-testid="spreadsheet-file-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setSpreadsheetFile(file);
                  // Reset so the same file can be picked again.
                  if (e.target) e.target.value = '';
                }}
              />
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => spreadsheetInputRef.current?.click()}
              >
                Choose spreadsheet
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h6">Loop Habit Tracker</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a <code>.db</code> backup file exported from Loop Habit
                Tracker. Imports habit definitions and their full completion
                history. Re-uploads are safe — existing habits are updated and
                duplicate logs are skipped.
              </Typography>
              <input
                ref={loopHabitsInputRef}
                type="file"
                accept=".db"
                style={{ display: 'none' }}
                onChange={(e) => handleUpload('loop-habits', e)}
              />
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => loopHabitsInputRef.current?.click()}
                disabled={busy === 'upload:loop-habits'}
              >
                {busy === 'upload:loop-habits' ? <CircularProgress size={20} /> : 'Upload .db'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={sectionGapSx} />

      <Typography variant="h5" sx={{ mb: 2 }}>
        Google accounts
      </Typography>
      <Grid container spacing={pageGridSpacing}>
        {(['google_calendar', 'google_tasks'] as Provider[]).map((provider) => {
          const account = accountFor(provider);
          const connected = Boolean(account);
          return (
            <Grid item xs={12} md={6} key={provider}>
              <Card>
                <CardContent sx={cardContentPaddingSx}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <GoogleIcon />
                    <Typography variant="h6">{PROVIDER_LABELS[provider]}</Typography>
                    <Chip
                      label={connected ? 'Connected' : 'Not connected'}
                      color={connected ? 'success' : 'default'}
                      size="small"
                    />
                  </Stack>
                  {connected ? (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Signed in as{' '}
                        <strong>{account?.account_email || 'unknown account'}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatSyncStamp(account?.last_sync_at)}
                        {account?.last_sync_status === 'error' && (
                          <> · last error: {account.last_sync_error}</>
                        )}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<CloudSyncIcon />}
                          onClick={() => handleSync(provider)}
                          disabled={busy === `sync:${provider}`}
                        >
                          {busy === `sync:${provider}` ? (
                            <CircularProgress size={20} />
                          ) : (
                            'Sync now'
                          )}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<LinkOffIcon />}
                          onClick={() => account && handleDisconnect(account)}
                          disabled={busy === `disconnect:${account?.id}`}
                        >
                          Disconnect
                        </Button>
                      </Stack>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Connect your Google account to pull {PROVIDER_LABELS[provider]} into{' '}
                        {provider === 'google_calendar' ? 'Planned Schedule' : 'Time Entries'}.
                        {provider === 'google_calendar'
                          ? ' Reading is all this grants. Publishing commitments back to your calendar is a separate permission you opt into below.'
                          : ' Read-only access — we never write back.'}
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<GoogleIcon />}
                        onClick={() => handleConnectGoogle(provider)}
                        disabled={busy === `connect:${provider}`}
                      >
                        {busy === `connect:${provider}` ? (
                          <CircularProgress size={20} />
                        ) : (
                          'Connect with Google'
                        )}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={sectionGapSx} />

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5">Calendar write-back</Typography>
        <Chip size="small" color={writeStatusChip.color} label={writeStatusChip.label} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Publishes the commitments you marked for publishing as real events on
        your Google Calendar, and only for a short window of upcoming days —
        everything further out lives inside this app and nowhere else. This is
        the one integration that changes something outside this app, so nothing
        here happens without you asking for it twice.
      </Typography>

      {readinessError && (
        <Alert severity="warning" sx={sectionGapSx}>
          {readinessError}. Until that resolves this page cannot say whether
          write-back is authorised — you can still run a preview, which sends
          nothing.
        </Alert>
      )}

      {readiness?.reconnect_required === true && (
        <Alert
          severity="info"
          icon={<GoogleIcon fontSize="inherit" />}
          sx={sectionGapSx}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => handleConnectGoogle('google_calendar')}
              disabled={busy === 'connect:google_calendar'}
            >
              {calendarUnconnected ? 'Connect Google Calendar' : 'Reconnect Google Calendar'}
            </Button>
          }
        >
          <AlertTitle>
            {calendarUnconnected
              ? 'Connect Google Calendar to enable calendar write-back'
              : 'Reconnect to enable calendar write-back'}
          </AlertTitle>
          {calendarUnconnected ? (
            <>
              There is no Google Calendar account connected yet, so there is
              nowhere to publish to. Connecting one grants this app permission
              to create and update the events it makes itself — and only those.
            </>
          ) : (
            <>
              Nothing is wrong with your connection and nothing has expired.
              It was granted read-only access, which is all this app used to ask
              for; write-back was added afterwards, so <em>every</em> account
              connected before now sits in exactly this state. Reconnecting
              grants permission to create and update the events this app itself
              makes, and nothing else — your existing calendar stays off limits.
            </>
          )}
        </Alert>
      )}

      <Card sx={sectionGapSx}>
        <CardContent sx={cardContentPaddingSx}>
          <Typography variant="h6">Sync preview (dry run)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A preview asks what a sync <em>would</em> do. It sends nothing to
            Google — no event is created, changed or removed, and nothing is
            recorded in the journal below. It deliberately works on a read-only
            connection too, because seeing the plan is how you decide whether
            granting write access is worth it.
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Window"
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              sx={{ minWidth: 150 }}
            >
              {WINDOW_DAY_CHOICES.map((days) => (
                <MenuItem key={days} value={days}>
                  Next {formatWindowDays(days)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<FactCheckIcon />}
              onClick={handlePreview}
              disabled={busy === 'calendar:preview'}
            >
              {busy === 'calendar:preview' ? (
                <CircularProgress size={20} />
              ) : (
                'Preview sync'
              )}
            </Button>
          </Stack>

          <Alert severity="info" icon={false} sx={{ mb: 2 }}>
            <AlertTitle>What can be touched</AlertTitle>
            {OWNERSHIP_PROMISE}
          </Alert>

          {preview && (
            <>
              {previewStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This plan was drawn up before the last apply, so it no longer
                  describes your calendar. Preview again to see where things
                  stand.
                </Alert>
              )}

              <Typography variant="subtitle1">{planSummary(preview)}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 2 }}
              >
                Covering the next {formatWindowDays(preview.window_days)}.
              </Typography>

              {preview.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No commitment in this window is marked for publishing, so
                  there is nothing to put on the calendar.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Action</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>When</TableCell>
                        <TableCell>Calendar</TableCell>
                        <TableCell>Why</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.items.map((item) => (
                        <TableRow
                          key={`${item.action}-${item.occurrence_id}-${item.external_event_id ?? 'new'}`}
                          hover
                        >
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={ACTION_COLORS[item.action] ?? 'default'}
                              label={ACTION_LABELS[item.action] ?? item.action}
                            />
                          </TableCell>
                          <TableCell>
                            {item.title || `Occurrence #${item.occurrence_id}`}
                          </TableCell>
                          <TableCell>{formatRange(item.start_at, item.end_at)}</TableCell>
                          <TableCell>{item.external_calendar_id}</TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                              {item.reason || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">Apply this plan</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Applying runs the plan above against your live calendar.{' '}
                {readiness?.ok === true
                  ? 'You will be asked to confirm before anything is sent.'
                  : 'It stays unavailable until this account grants write access.'}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<PublishIcon />}
                onClick={() => setConfirmApplyOpen(true)}
                disabled={
                  busy === 'calendar:apply' ||
                  readiness?.ok !== true ||
                  previewStale ||
                  actionableCount === 0
                }
              >
                {busy === 'calendar:apply' ? (
                  <CircularProgress size={20} />
                ) : (
                  'Apply to calendar'
                )}
              </Button>

              {applyResult && (
                <Alert
                  severity={applyResult.failures > 0 ? 'warning' : 'success'}
                  sx={{ mt: 2 }}
                  onClose={() => setApplyResult(null)}
                >
                  <AlertTitle>
                    {applyResult.applied
                      ? 'Applied to your calendar'
                      : 'Nothing was changed'}
                  </AlertTitle>
                  {applySummary(applyResult)}
                  {applyResult.applied && applyResult.detail
                    ? ` ${applyResult.detail}`
                    : null}
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={cardContentPaddingSx}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Write journal
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshJournal}
              disabled={busy === 'calendar:journal'}
            >
              {busy === 'calendar:journal' ? <CircularProgress size={18} /> : 'Refresh'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Every change this app makes to your calendar is written down twice —
            an <strong>intent</strong> row before the call goes out, and a{' '}
            <strong>result</strong> row after it comes back. The log is
            append-only, so an intent with no result is left standing rather
            than tidied away: it means the attempt was made and its outcome is
            genuinely unknown. Newest first.
          </Typography>

          {journalError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {journalError}
            </Alert>
          )}

          {journal.length === 0
            ? !journalError && (
                <Typography variant="body2" color="text.secondary">
                  Nothing has been written to your calendar yet. Previews never
                  appear here — they send nothing.
                </Typography>
              )
            : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Phase</TableCell>
                        <TableCell>Outcome</TableCell>
                        <TableCell>Event</TableCell>
                        <TableCell>Detail</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {journal.map((entry) => {
                        const outcome = journalOutcome(entry, unresolvedIntents);
                        return (
                          <TableRow key={entry.id} hover>
                            <TableCell>{formatStamp(entry.created_at)}</TableCell>
                            <TableCell>
                              {ACTION_LABELS[entry.action] ?? entry.action}
                            </TableCell>
                            <TableCell>
                              {PHASE_LABELS[entry.phase] ?? entry.phase}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                color={outcome.color}
                                label={outcome.label}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {entry.occurrence_id != null
                                  ? `Occurrence #${entry.occurrence_id}`
                                  : '—'}
                              </Typography>
                              {entry.external_event_id && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {entry.external_event_id}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 360 }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                                {entry.reason ||
                                  entry.response_summary ||
                                  entry.request_summary ||
                                  '—'}
                              </Typography>
                              {entry.error && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  display="block"
                                  sx={{ whiteSpace: 'normal' }}
                                >
                                  {entry.error}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
        </CardContent>
      </Card>

      <Dialog
        open={confirmApplyOpen}
        onClose={() => setConfirmApplyOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Apply this plan to your live calendar?</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {preview ? planSummary(preview) : 'Run a preview first.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {OWNERSHIP_PROMISE}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApplyOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={busy === 'calendar:apply'}
          >
            Yes, apply now
          </Button>
        </DialogActions>
      </Dialog>

      <SpreadsheetImportDialog
        open={Boolean(spreadsheetFile)}
        file={spreadsheetFile}
        onClose={() => setSpreadsheetFile(null)}
        onImported={(message) => {
          setInfo(message);
          setSpreadsheetFile(null);
        }}
      />
    </PageShell>
  );
};

export default Integrations;
